<template>
  <e-dialog v-model="isOpened" wide @close="onDialogClose" @open="setItems">
    <template #title>
      <div class="caption">Групповая операция</div>
    </template>

    <div class="paragraph-s_h text-secondary mb-6">
      Отредактируйте тарифы и при необходимости периоды для выбранных вариантов размещения. <br />
      При необходимости закрытия продажи на период перейдите в раздел
      <router-link class="link" :to="{ name: RouteNames.TariffPricesAndRestrictions, query: { tab: 2 } }"
        >Ограничения ВР</router-link
      >
      /
      <router-link class="link" :to="{ name: RouteNames.TariffPricesAndRestrictions, query: { tab: 3 } }"
        >Ограничения ГУ</router-link
      >
      / <router-link class="link" :to="{ name: RouteNames.TariffQuota }">Квота номеров</router-link>.
    </div>

    <template #close>
      <div v-if="$vuetify.display.mdAndUp" class="d-flex">
        <e-btn variant="secondary" @click="cancel"> Отмена</e-btn>
        <e-btn variant="primary" class="ml-4" :loading="isEditGridUpdateLoading" @click="save"> Применить</e-btn>
      </div>
    </template>

    <template #buttons>
      <div v-if="!$vuetify.display.mdAndUp" class="d-flex justify-end w-100">
        <e-btn variant="secondary" @click="cancel"> Отмена</e-btn>
        <e-btn variant="primary" class="ml-4" :loading="isEditGridUpdateLoading" @click="save"> Применить</e-btn>
      </div>
    </template>

    <tariffs-table
      ref="tableRef"
      v-model="items"
      v-model:is-valid="isValid"
      :max="tariffScheduleGrid?.baseSettings.period.to"
      with-dates
    />
  </e-dialog>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { nextTick, ref } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute } from 'vue-router';
import type { ChessDateCellType } from '@/core/components/interface/Chess/Chess.type';
import type { InitialValuesItem, UpdateTariffGridLines } from '@/parts/tariffs/types/tariffs';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn } from '@/core/components/ui';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { useTourStore } from '@/core/store/tour';
import { getDateWithUtcOffset, getIsoDateFormat } from '@/core/utils/date';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import TariffsTable from '@/parts/tariffs/components/PricesAndRestrictions/TariffsTable.vue';
import { usePricesStore } from '@/parts/tariffs/store/prices';
import { touristPlaceName, touristTypeString } from '@/parts/tariffs/utils/tariffs';

const props = defineProps<{
  selectedItems: ChessDateCellType[] | undefined;
}>();

const emit = defineEmits<{
  (event: 'close'): void;
}>();

const $route = useRoute();

const { activeAccount, timeZoneOffsetMin } = useAccommodationObjectsStore();
const { editGridCancel, tariffScheduleGrid, editGridUpdate, isEditGridUpdateLoading } = usePricesStore();

const { closeActiveTour } = useTourStore();

const onDialogClose = async () => {
  emit('close');

  await editGridCancel();
};

const items = ref<InitialValuesItem[]>([]);

const setItems = () => {
  let services = tariffScheduleGrid.value?.tabs?.grid?.services;

  if (!services) {
    return;
  }

  if (props.selectedItems?.length) {
    services = services.filter((service) =>
      props.selectedItems?.some((item) => String(item.id)?.startsWith(service.serviceGroup.cid)),
    );
  }
  const dates = props.selectedItems?.map((item) => dayjs(item.date));

  const startDate = dates?.length
    ? dayjs.min(dates)?.toDate()
    : getDateWithUtcOffset(new Date(), timeZoneOffsetMin.value);
  const endDate = dates?.length
    ? dayjs.max(dates)?.toDate()
    : dayjs(tariffScheduleGrid.value!.baseSettings.period!.to).toDate();

  items.value = services.map((service) => ({
    title: service.serviceGroup.name[0].value,
    id: service.serviceGroup.cid,
    subgroups: service.roomCategories
      .filter(
        (roomCategory) =>
          !props.selectedItems?.length ||
          props.selectedItems?.some((item) => String(item.id)?.includes(roomCategory.roomCategory.cid)),
      )
      .map((roomCategory) => {
        const items = roomCategory.placingFactors
          .filter(
            (_, index) =>
              !props.selectedItems?.length ||
              props.selectedItems.some(
                (item) =>
                  String(item.id)?.includes(roomCategory.roomCategory.cid) &&
                  String(item.id)?.includes(`placing_${index}`),
              ),
          )
          .map((placingFactor) => {
            const item = {
              startDate: startDate!,
              endDate: endDate!,
              tariff: null,
            };

            const addItemInfo = placingFactor.placingFactor.touristPlace
              ? {
                  name: touristPlaceName(placingFactor.placingFactor.touristPlace?.touristPlace?.isAdditional),
                  type: touristTypeString(placingFactor.placingFactor.touristPlace),
                  variant: placingFactor.placingFactor.touristPlace?.relatedRoomPlacings || [],
                  touristPlace: {
                    touristTypeCid: placingFactor.placingFactor.touristPlace.touristPlace.touristTypeCID,
                    isAdditional: placingFactor.placingFactor.touristPlace.touristPlace.isAdditional,
                  },
                }
              : {
                  name: 'Весь номер',
                  type: 'Весь номер',
                  variant: [placingFactor.placingFactor.roomPlacing?.placingVariant?.name || ''],
                  roomPlacingCid: placingFactor.placingFactor.roomPlacing?.cid,
                };

            return { ...item, ...addItemInfo };
          });

        return {
          title: roomCategory.roomCategory.name[0].value,
          id: roomCategory.roomCategory.cid,
          items,
          selected: items,
        };
      }),
  }));
};

const isOpened = defineModel<boolean>();

const cancel = () => {
  isOpened.value = false;
  closeActiveTour();
};

onBeforeRouteUpdate(() => {
  cancel();
});

onBeforeRouteLeave(() => {
  cancel();
});

const isValid = ref(false);
const tableRef = ref();

const save = async () => {
  await tableRef.value?.validate();

  await nextTick();

  if (!isValid.value) {
    return;
  }

  const result = await editGridUpdate({
    input: {
      tariffScheduleCid: String($route.params.t_id),
      lines: items.value.reduce<UpdateTariffGridLines>((res, item) => {
        const rows = item.subgroups.reduce<UpdateTariffGridLines>((subgroupsRes, sub) => {
          subgroupsRes = [
            ...subgroupsRes,
            ...sub.selected!.map((subItem) => ({
              schedulePeriod: {
                from: getIsoDateFormat(subItem.startDate!),
                to: getIsoDateFormat(subItem.endDate!),
              },
              rate: {
                currencyISOCode: activeAccount.value!.currencyISOCode,
                value: subItem.tariff as number,
              },
              tariffFactor: {
                serviceGroupCid: item.id,
                roomCategoryCid: sub.id,
                placingFactor: {
                  placingVariantCid: subItem.roomPlacingCid,
                  touristPlacing: subItem.touristPlace,
                },
              },
            })),
          ];

          return subgroupsRes;
        }, []);

        res = [...res, ...rows];

        return res;
      }, []),
    },
  });

  if (result.errors) {
    return;
  }

  cancel();
};
</script>

<style scoped lang="scss"></style>
